import { DropLocation, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { P2GBox, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useCallback, useContext, useMemo, useState } from 'react';
import { PickupShopId } from '~/apps/smartsend/lib/consts/ItemMetaConstants';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ClosestPickUpShops from './ClosestPickUpShops';
import { SmartSendContext } from '~/apps/smartsend/lib/context/SmartSendContext';
import { SaveItemsAction } from '~/apps/smartsend/lib/context/SmartSendContextModels';
import P2GContentDropDown from '../../P2GContentDropDown';
import { P2GSidePanel } from '~/components/sidepanel';

interface props {
  item: SmartSendItemApiModel;
  isXs: boolean;
  wrapper: (children: React.ReactNode) => React.ReactNode;
}

export default function ClosestPickUpDropShop({ item, isXs, wrapper }: props) {
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { dispatch } = useContext(SmartSendContext);

  const preferred = useMemo(() => {
    if (item.Meta?.[PickupShopId]?.Value) {
      return item.State?.validation?.QuoteState?.Locations?.PickupLocation;
    }
    return null;
  }, [item.Meta, item.State?.validation?.QuoteState?.Locations?.PickupLocation]);

  const onSave = useCallback(
    (location: DropLocation) => {
      const meta = { ...item.Meta, [PickupShopId]: { Type: 'String', Value: location.Id.toString() } };
      dispatch(
        new SaveItemsAction([item.Id], ['Meta'], {
          Meta: meta
        })
      );
    },
    [dispatch, item]
  );

  if (!preferred) {
    return null;
  }

  return wrapper(
    <P2GBox
      sx={{
        textAlign: 'center'
      }}
    >
      <P2GText variant="caption">
        {t('RECIPIENT_COLLECT_PARCEL_FROM', 'Your recipient will need to collect their parcel from:')}{' '}
        <strong>
          {preferred.Name.length > 30
            ? `${preferred.Name}, ${preferred.Postcode}`
            : `${preferred.Name} - ${preferred.Address1}, ${preferred.Postcode}`}
        </strong>
        {' - '}
        {isXs ? (
          <>
            <P2GLink onClick={() => setSidePanelOpen(true)}>{t('CHANGE_BUTTON', 'Change')}</P2GLink>
            <P2GSidePanel
              onClose={() => setSidePanelOpen(false)}
              open={sidePanelOpen}
              title={t('OTHER_DELIVERY_SHOPS_NEAR_RECIPIENT_TITLE', 'Other options near your recipient')}
            >
              <ClosestPickUpShops item={item} onSelectLocation={onSave} selected={preferred.Id} />
            </P2GSidePanel>
          </>
        ) : (
          <P2GContentDropDown
            width="350px"
            children={<ClosestPickUpShops item={item} onSelectLocation={onSave} selected={preferred.Id} />}
            control={<P2GLink>{t('CHANGE_BUTTON', 'Change')}</P2GLink>}
          />
        )}
      </P2GText>
    </P2GBox>
  );
}
